/* Предпросмотр вспомогательных файлов: что показываем и как не пустить в
   страницу чужую разметку.

   Основной файл документа — PDF: его рассматривают, на нём ставят замечания,
   его сравнивают версиями. Excel и Word подрядчик прикладывает ВСПОМОГАТЕЛЬНО
   — расчёт, ведомость, пояснительная записка, — и нужда у них одна: посмотреть,
   не скачивая. Замечаний по ним нет: замечание привязано к листу и координатам
   PDF, а у таблицы ни листов, ни координат. */

/** Чем показывать файл. `other` — прежняя карточка со скачиванием. */
export type PreviewKind = 'pdf' | 'sheet' | 'doc' | 'other'

/**
 * Вид файла по его имени.
 *
 * <p>Имя, а не media_type: тип приходит из браузера подрядчика при загрузке и
 * бывает и `application/octet-stream`, и пустым. Расширение врёт реже, а
 * ошибка разбора всё равно вернёт карточку со скачиванием — то есть цена
 * ошибки мала.
 *
 * <p>`.doc` и `.xls` разведены намеренно: старый двоичный `.doc` не читает
 * никакая браузерная библиотека, а `.xls` читает та же, что и `.xlsx`.
 */
export function previewKind(name: string | null | undefined): PreviewKind {
  /* Имени НЕТ вовсе — файл считается основным: ровно так вело себя прежнее
     условие (`fileName == null || /\.pdf$/i`), и менять это поведение задачей
     предпросмотра незачем. Имя БЕЗ расширения — другое дело: показать его
     нечем, и оно уходит на скачивание, как и уходило. */
  if (name == null) return 'pdf'
  const ext = name.toLowerCase().match(/\.([a-z0-9]+)$/)?.[1]
  if (!ext) return 'other'
  if (ext === 'pdf') return 'pdf'
  if (ext === 'xlsx' || ext === 'xlsm' || ext === 'xls') return 'sheet'
  if (ext === 'docx') return 'doc'
  return 'other'
}

/** Узлы, которые переживают чистку. Всё прочее вырезается вместе с содержимым. */
const ALLOWED = new Set([
  'P', 'BR', 'STRONG', 'B', 'EM', 'I', 'U', 'SUP', 'SUB',
  'H1', 'H2', 'H3', 'H4', 'H5', 'H6',
  'UL', 'OL', 'LI',
  'TABLE', 'THEAD', 'TBODY', 'TR', 'TD', 'TH',
  'A', 'DIV', 'SPAN',
])

/** Схемы, по которым ссылке позволено вести. */
const SAFE_HREF = /^(https?:|mailto:)/i

/**
 * Почистить HTML, полученный из файла ПОДРЯДЧИКА.
 *
 * <p>Word разбирается библиотекой, и на выходе у неё HTML. Вставить его как
 * есть — значит выполнить в сеансе заказчика разметку, которую прислала другая
 * сторона: обработчик события в атрибуте, `javascript:` в ссылке, `<script>` в
 * теле. Документооборот — ровно то место, где чужой файл приходит каждый день
 * и от организации, с которой идёт спор.
 *
 * <p>Чистка по БЕЛОМУ списку, а не по чёрному: перечислить всё опасное нельзя,
 * список дыр растёт вместе с браузерами. Неизвестный узел вырезается целиком.
 * Атрибуты снимаются ВСЕ, кроме `href` у ссылки, и та обязана вести по
 * http/https/mailto — иначе ссылка остаётся текстом.
 *
 * <p>Правило одно и в одном месте: два места разошлись бы на первой же правке,
 * и разошлись бы молча.
 */
export function sanitizeHtml(html: string): string {
  const doc = new DOMParser().parseFromString(html, 'text/html')
  const walk = (node: Element) => {
    for (const child of [...node.children]) {
      if (!ALLOWED.has(child.tagName)) {
        child.remove()
        continue
      }
      for (const attr of [...child.attributes]) {
        const keep = child.tagName === 'A' && attr.name.toLowerCase() === 'href'
          && SAFE_HREF.test(attr.value.trim())
        if (!keep) child.removeAttribute(attr.name)
      }
      /* Чужая вкладка, а не эта: ссылка из присланного документа не должна
         уводить со страницы вместе с несохранённой работой. */
      if (child.tagName === 'A' && child.getAttribute('href')) {
        child.setAttribute('target', '_blank')
        child.setAttribute('rel', 'noopener noreferrer')
      }
      walk(child)
    }
  }
  walk(doc.body)
  return doc.body.innerHTML
}
